/**
 * Create or edit one schedule (task 1003, mockup §D and §E).
 *
 * An INLINE panel that replaces the list while it is open — the pattern Testing
 * already uses for Generate Tests — so the page header, the tab strip and the
 * Config section rail all stay put. No modal, no route of its own.
 *
 * Two shapes behind one form: a recurring rule (preset chips over a five-field
 * cron, read in an IANA timezone) or a single instant. The preset chips fill the
 * expression and the expression stays visible, so the sentence a User picked and
 * the string the clock reads are never two different claims.
 *
 * **The next-three-runs preview is the confirmation step**, not decoration: a
 * cron expression is unreadable, and the only reliable way to let someone agree
 * to one is to show what it produces, in the zone they chose, before they save.
 * It is computed with the same cron library the service's clock uses
 * (`src/lib/scheduleCron.ts`).
 *
 * The saved input reuses the scenario editor — `DynamicSchemaForm` with the
 * Form / JSON toggle — so the two surfaces that hold a request body for this
 * service cannot drift. It is validated against the AI Service's input schema
 * by the SERVICE on save; its field-level refusals are rendered here rather
 * than summarised.
 */
import { useMemo, useState } from 'react';
import { CodeBracketIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { DynamicSchemaForm } from '@/components/runner/DynamicSchemaForm';
import { FormField, FormSelect, FormTextArea } from '@/components/shared/form';
import { useEnvironments } from '@/hooks/useEnvironments';
import { useVersioningEnabled } from '@/hooks/useResourceVersions';
import { useCreateSchedule, useUpdateSchedule } from '@/hooks/useSchedules';
import { useOptionalToast } from '@/contexts/ToastContext';
import {
  browserTimezone,
  buildCron,
  describeInterval,
  formatInTimezone,
  instantToWallClock,
  nextOccurrences,
  validateCron,
  wallClockToInstant,
  PREVIEW_OCCURRENCE_COUNT,
} from '@/lib/scheduleCron';
import { isSchemaRenderable } from '@/utils/schemaUtils';
import type { Endpoint } from '@/types';
import type { CreateScheduleInput, Schedule, ScheduleKind } from '@/types/schedules';

/** The rules the chips offer. `custom` is the escape hatch to the raw fields. */
type RulePreset = 'hourly' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom';

const RULE_PRESETS: Array<{ id: RulePreset; label: string }> = [
  { id: 'hourly', label: 'Every hour' },
  { id: 'daily', label: 'Every day at…' },
  { id: 'weekdays', label: 'Weekdays at…' },
  { id: 'weekly', label: 'Weekly on…' },
  { id: 'monthly', label: 'Monthly on the…' },
  { id: 'custom', label: 'Custom cron' },
];

const WEEKDAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, index) => ({
  value: String(index + 1),
  label: String(index + 1),
}));

/**
 * Zones to offer. `Intl.supportedValuesOf` is the browser's own IANA list; where
 * it is missing (jsdom, older runtimes) the viewer's own zone plus UTC is enough
 * to save a schedule, and a zone the list does not carry can still arrive on an
 * existing row without being dropped.
 */
const timezoneOptions = (current: string): Array<{ value: string; label: string }> => {
  const supported =
    typeof (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf === 'function'
      ? (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf(
          'timeZone'
        )
      : [];
  const zones = new Set<string>([current, 'UTC', ...supported]);
  return [...zones].filter(Boolean).map((zone) => ({ value: zone, label: zone }));
};

/** Which preset an existing expression came from, so editing reopens on it. */
const presetForCron = (cron: string | null): RulePreset => {
  if (!cron) return 'daily';
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) return 'custom';
  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
  const numeric = (value: string) => /^\d+$/.test(value);
  if (month !== '*') return 'custom';
  if (numeric(minute) && hour === '*' && dayOfMonth === '*' && dayOfWeek === '*') return 'hourly';
  if (numeric(minute) && numeric(hour) && dayOfMonth === '*') {
    if (dayOfWeek === '*') return 'daily';
    if (dayOfWeek === '1-5') return 'weekdays';
    if (numeric(dayOfWeek)) return 'weekly';
  }
  if (numeric(minute) && numeric(hour) && numeric(dayOfMonth) && dayOfWeek === '*') return 'monthly';
  return 'custom';
};

/** `HH:MM` out of a cron's hour and minute fields, for the time picker. */
const timeFromCron = (cron: string | null): string => {
  const fields = (cron ?? '').trim().split(/\s+/);
  if (fields.length !== 5) return '09:00';
  const [minute, hour] = fields;
  if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour)) return '09:00';
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
};

interface ScheduleFormProps {
  endpoint: Endpoint;
  /** The row being edited; absent for a new schedule. */
  schedule?: Schedule;
  onClose: () => void;
  /** The plan's interval floor, when the account's limits have resolved. */
  minIntervalSeconds?: number | null;
}

export function ScheduleForm({
  endpoint,
  schedule,
  onClose,
  minIntervalSeconds,
}: ScheduleFormProps) {
  const toast = useOptionalToast();
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const { enabled: publishingEnabled } = useVersioningEnabled();
  const { environments } = useEnvironments();

  const inputSchema = (endpoint.input ?? null) as Record<string, unknown> | null;
  const canRenderAsForm = !!inputSchema && isSchemaRenderable(inputSchema);

  const [name, setName] = useState(schedule?.name ?? '');
  const [description, setDescription] = useState(schedule?.description ?? '');
  const [kind, setKind] = useState<ScheduleKind>(schedule?.kind ?? 'recurring');
  const [timezone, setTimezone] = useState(schedule?.timezone ?? browserTimezone());
  const [preset, setPreset] = useState<RulePreset>(presetForCron(schedule?.cron ?? null));
  const [time, setTime] = useState(timeFromCron(schedule?.cron ?? null));
  const [weekday, setWeekday] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [customCron, setCustomCron] = useState(schedule?.cron ?? '0 9 * * 1-5');
  const initialWallClock = schedule?.runAt
    ? instantToWallClock(new Date(schedule.runAt), schedule.timezone ?? browserTimezone())
    : null;
  const [onceDate, setOnceDate] = useState(initialWallClock?.date ?? '');
  const [onceTime, setOnceTime] = useState(initialWallClock?.time ?? '09:00');
  const [environmentId, setEnvironmentId] = useState(schedule?.environmentId ?? '');
  const [inputValues, setInputValues] = useState<Record<string, unknown>>(schedule?.input ?? {});
  const [inputJson, setInputJson] = useState(JSON.stringify(schedule?.input ?? {}, null, 2));
  const [inputViewMode, setInputViewMode] = useState<'form' | 'json'>(
    canRenderAsForm ? 'form' : 'json'
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /** The expression the rule currently means — presets fill it, Custom owns it. */
  const cron = useMemo(() => {
    if (preset === 'custom') return customCron;
    const [hourPart, minutePart] = time.split(':');
    const hour = Number(hourPart);
    const minute = Number(minutePart);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return customCron;
    switch (preset) {
      case 'hourly':
        // Every hour, on the minute the time picker's minutes name (0 by
        // default) — the hour field is what "every hour" actually means.
        return `${minute} * * * *`;
      case 'daily':
        return buildCron({ minute, hour });
      case 'weekdays':
        return buildCron({ minute, hour, dayOfWeek: '1-5' });
      case 'weekly':
        return buildCron({ minute, hour, dayOfWeek: weekday });
      case 'monthly':
        return buildCron({ minute, hour, dayOfMonth });
      default:
        return customCron;
    }
  }, [preset, time, weekday, dayOfMonth, customCron]);

  const cronValidation = kind === 'recurring' ? validateCron(cron, timezone) : { valid: true };
  const onceInstant = kind === 'once' ? wallClockToInstant(onceDate, onceTime, timezone) : null;
  const onceIsPast = !!onceInstant && onceInstant.getTime() <= Date.now();

  /** The three instants the rule produces next — or the one a once-schedule has. */
  const preview: Date[] = useMemo(() => {
    if (kind === 'once') return onceInstant && !onceIsPast ? [onceInstant] : [];
    if (!cronValidation.valid) return [];
    return nextOccurrences(cron, timezone, PREVIEW_OCCURRENCE_COUNT);
  }, [kind, onceInstant, onceIsPast, cron, timezone, cronValidation.valid]);

  const environmentOptions = [
    { value: '', label: 'Bare URL (no environment)' },
    ...environments.map((environment) => ({
      value: environment.id,
      label: environment.name,
    })),
  ];

  const parsedInput = (): Record<string, unknown> | null => {
    if (inputViewMode === 'form' && canRenderAsForm) return inputValues;
    try {
      const parsed = JSON.parse(inputJson || '{}');
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  const toggleInputView = () => {
    if (inputViewMode === 'form') {
      setInputJson(JSON.stringify(inputValues, null, 2));
      setInputViewMode('json');
      return;
    }
    try {
      const parsed = JSON.parse(inputJson || '{}');
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        setInputValues(parsed as Record<string, unknown>);
      }
    } catch {
      // A JSON body that does not parse cannot become form values; the JSON
      // error below says so and the toggle stays where it is.
      setFieldErrors((prev) => ({ ...prev, input: 'That is not valid JSON' }));
      return;
    }
    setInputViewMode('form');
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Give the schedule a name';
    if (kind === 'recurring' && !cronValidation.valid) {
      errors.cron = cronValidation.error ?? 'That rule is not a valid cron expression';
    }
    if (kind === 'once') {
      if (!onceInstant) errors.runAt = 'Pick a date and a time';
      else if (onceIsPast) {
        errors.runAt = 'That time has already passed. Pick a moment at least one minute from now.';
      }
    }
    if (!parsedInput()) errors.input = 'That is not valid JSON';
    return errors;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const input = parsedInput() ?? {};
    const timing: Partial<CreateScheduleInput> =
      kind === 'recurring'
        ? { kind, cron, timezone }
        : { kind, runAt: onceInstant!.toISOString(), timezone };

    try {
      if (schedule) {
        await updateSchedule.mutateAsync({
          id: schedule.id,
          data: {
            name: name.trim(),
            description: description.trim(),
            input,
            environmentId: environmentId || null,
            ...timing,
          },
        });
        toast?.success('Schedule saved');
      } else {
        await createSchedule.mutateAsync({
          endpointId: endpoint.id,
          name: name.trim(),
          description: description.trim() || undefined,
          input,
          ...(environmentId ? { environmentId } : {}),
          ...timing,
        } as CreateScheduleInput);
        toast?.success('Schedule created');
      }
      onClose();
    } catch (error) {
      // Field-level refusals are rendered ON the fields they name; the plan
      // refusals (402/403) are already on screen through the EntitlementGuard,
      // so nothing is restated here.
      const refusal = error as Error & {
        status?: number;
        validationErrors?: Array<{ field: string; message: string }>;
      };
      if (refusal.validationErrors?.length) {
        const mapped: Record<string, string> = {};
        refusal.validationErrors.forEach((detail) => {
          const key = detail.field.startsWith('input') ? 'input' : detail.field;
          mapped[key] = mapped[key] ? `${mapped[key]} ${detail.message}` : detail.message;
        });
        setFieldErrors(mapped);
      } else if (refusal.status !== 402 && refusal.status !== 403) {
        toast?.error('Could not save the schedule', refusal.message);
      }
    }
  };

  const saving = createSchedule.isPending || updateSchedule.isPending;

  return (
    <section
      data-testid="schedule-form"
      className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {schedule ? 'Edit schedule' : 'New schedule'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Runs under this Account with the input saved here, exactly as an API caller would.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="schedule-form-back"
          className="text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
        >
          ← Back to schedules
        </button>
      </div>

      <form className="mt-6 space-y-6" onSubmit={submit}>
        <FormField
          label="Name"
          name="schedule-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={fieldErrors.name}
          required
          placeholder="Weekday digest"
        />

        <FormTextArea
          label="Description"
          name="schedule-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={2}
          placeholder="What this schedule is for"
        />

        {/* Runs: the one choice that changes the shape of everything below it. */}
        <div>
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Runs
          </span>
          <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600">
            {(
              [
                { id: 'recurring' as const, label: 'On a recurring rule' },
                { id: 'once' as const, label: 'Once, at a set time' },
              ]
            ).map((option, index) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setKind(option.id)}
                aria-pressed={kind === option.id}
                data-testid={`schedule-kind-${option.id}`}
                className={`px-3 py-1.5 text-sm font-medium ${index === 0 ? 'rounded-l-md' : 'rounded-r-md'} ${
                  kind === option.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {kind === 'recurring' ? (
          <div className="space-y-3">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Rule</span>
            <div className="flex flex-wrap gap-2">
              {RULE_PRESETS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setPreset(option.id)}
                  aria-pressed={preset === option.id}
                  data-testid={`schedule-preset-${option.id}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${
                    preset === option.id
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-900/40 dark:text-indigo-200'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-end gap-3">
              {preset !== 'custom' && preset !== 'hourly' && (
                <div>
                  <label
                    htmlFor="schedule-time"
                    className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Time
                  </label>
                  <input
                    id="schedule-time"
                    type="time"
                    value={time}
                    onChange={(event) => setTime(event.target.value)}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              )}

              {preset === 'weekly' && (
                <FormSelect
                  label="Day"
                  name="schedule-weekday"
                  value={weekday}
                  onChange={(event) => setWeekday(event.target.value)}
                  options={WEEKDAY_OPTIONS}
                />
              )}

              {preset === 'monthly' && (
                <FormSelect
                  label="Day of the month"
                  name="schedule-day-of-month"
                  value={dayOfMonth}
                  onChange={(event) => setDayOfMonth(event.target.value)}
                  options={DAY_OF_MONTH_OPTIONS}
                />
              )}

              <FormSelect
                label="Timezone"
                name="schedule-timezone"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                options={timezoneOptions(timezone)}
              />
            </div>

            <div>
              {preset === 'custom' ? (
                <>
                  <label
                    htmlFor="schedule-cron"
                    className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Cron expression
                  </label>
                  <input
                    id="schedule-cron"
                    data-testid="schedule-cron"
                    type="text"
                    value={cron}
                    onChange={(event) => setCustomCron(event.target.value)}
                    className={`w-full rounded-md border px-3 py-2 font-mono text-sm dark:bg-gray-700 dark:text-white ${
                      fieldErrors.cron
                        ? 'border-red-400 dark:border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                </>
              ) : (
                <>
                  <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Cron expression
                  </span>
                  <code
                    data-testid="schedule-cron"
                    className="block font-mono text-sm text-gray-700 dark:text-gray-200"
                  >
                    {cron}
                  </code>
                </>
              )}
              {/* A service refusal (e.g. the plan interval floor) can land on the
                  cron under a preset too, so the message sits outside the branch. */}
              {fieldErrors.cron && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.cron}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Timezone defaults to your browser's. The rule follows daylight-saving changes in
                that zone.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">When</span>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label
                  htmlFor="schedule-date"
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Date
                </label>
                <input
                  id="schedule-date"
                  type="date"
                  value={onceDate}
                  onChange={(event) => setOnceDate(event.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label
                  htmlFor="schedule-once-time"
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Time
                </label>
                <input
                  id="schedule-once-time"
                  type="time"
                  value={onceTime}
                  onChange={(event) => setOnceTime(event.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <FormSelect
                label="Timezone"
                name="schedule-timezone"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                options={timezoneOptions(timezone)}
              />
            </div>
            {fieldErrors.runAt && (
              <p data-testid="schedule-once-error" className="text-xs text-red-600 dark:text-red-400">
                {fieldErrors.runAt}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              A once-schedule fires one time and then shows as Completed with a link to its run.
            </p>
          </div>
        )}

        {/* The confirmation step. Nothing else on this form tells a User what a
            cron expression actually means. */}
        <div
          data-testid="schedule-preview"
          className="rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
        >
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {kind === 'once' ? 'It runs' : `Next ${PREVIEW_OCCURRENCE_COUNT} runs`}
          </p>
          {preview.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Nothing to preview yet — finish the rule above.
            </p>
          ) : (
            <ul className="mt-2 space-y-1">
              {preview.map((instant) => (
                <li
                  key={instant.toISOString()}
                  className="font-mono text-xs text-gray-700 dark:text-gray-300"
                >
                  {formatInTimezone(instant, timezone)}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {typeof minIntervalSeconds === 'number' && minIntervalSeconds > 0
              ? `Your plan allows a run at most ${describeInterval(minIntervalSeconds)}. `
              : ''}
            If a run is still going when the next one is due, that occurrence is skipped, not
            queued.
          </p>
        </div>

        {/* Which release to run. Absent Publishing there is only one answer, so
            the field does not render at all. */}
        {publishingEnabled && (
          <FormSelect
            label="Environment"
            name="schedule-environment"
            value={environmentId}
            onChange={(event) => setEnvironmentId(event.target.value)}
            options={environmentOptions}
          />
        )}

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Input</span>
            {canRenderAsForm && (
              <button
                type="button"
                onClick={toggleInputView}
                data-testid="schedule-input-toggle"
                className="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                {inputViewMode === 'form' ? (
                  <>
                    <CodeBracketIcon className="mr-1.5 h-4 w-4" />
                    Edit as JSON
                  </>
                ) : (
                  <>
                    <Squares2X2Icon className="mr-1.5 h-4 w-4" />
                    Use Form
                  </>
                )}
              </button>
            )}
          </div>

          {inputViewMode === 'form' && canRenderAsForm && inputSchema ? (
            <DynamicSchemaForm
              schema={inputSchema}
              values={inputValues}
              onChange={setInputValues}
            />
          ) : (
            <FormTextArea
              label="JSON body"
              name="schedule-input-json"
              value={inputJson}
              onChange={(event) => setInputJson(event.target.value)}
              error={fieldErrors.input}
              rows={8}
              placeholder='{"key": "value"}'
            />
          )}
          {inputViewMode === 'form' && fieldErrors.input && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.input}</p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            The same editor the Testing tab's scenarios use. It is validated against the schema in
            Input / Output / Mapping when you save.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="schedule-form-save"
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save schedule'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            The schedule takes effect within a minute of saving.
          </span>
        </div>
      </form>
    </section>
  );
}

export default ScheduleForm;
